/**
 * Os hooks de TanStack Query.
 *
 * Eles sao **casca sobre `queries.ts`**: cada hook e um `useQuery(queries.x.y(...))`. A
 * substancia — chave, `queryFn`, `staleTime`, `gcTime` — mora na fabrica pura, e e la que ela
 * e testada. Um hook que decidisse `staleTime` por conta propria seria uma segunda politica de
 * cache, e o `PWA §4.2` e explicito ao querer uma so (X-05).
 *
 * ## §51 do briefing: nenhuma regra de negocio aqui
 *
 * Nao ha soma, nao ha comparacao de preco, nao ha decisao de permissao. O que estes hooks fazem
 * e pedir e devolver. `isFetching`, `error` e `data` sao estado de rede, nao regra.
 *
 * ## Por que `@tanstack/react-query` e peer OPCIONAL
 *
 * O cliente HTTP tambem roda onde nao ha React: a drenagem da `outbox` (`PWA §8.4`) acontece no
 * evento `sync` do service worker, e o kill switch (`PWA §5.5`) precisa falar com a API sem
 * arvore de componentes. Este arquivo e o unico que importa React — quem nao usa hooks nao
 * paga por eles.
 */
import { useMutation, useQuery, type UseMutationResult } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { ApiClient } from '../client.js';

import { STORE_QUEUE_POLL_INTERVAL_MS } from './cache-policy.js';
import { createQueries, type ApiQueries } from './queries.js';

interface ApiContextValue {
  readonly client: ApiClient;
  readonly queries: ApiQueries;
}

const ApiContext = createContext<ApiContextValue | null>(null);

export interface ApiClientProviderProps {
  readonly client: ApiClient;
  readonly children: ReactNode;
}

/**
 * Injeta o cliente na arvore.
 *
 * Um `createApiClient` em modulo global funcionaria e foi descartado: o teste de componente
 * precisa trocar o cliente por um duble, e singleton de modulo obriga a mexer no registro de
 * modulos do runner para conseguir isso.
 */
export function ApiClientProvider({ client, children }: ApiClientProviderProps): ReactNode {
  const value = useMemo<ApiContextValue>(
    () => ({ client, queries: createQueries(client) }),
    [client],
  );
  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

function useApiContext(): ApiContextValue {
  const value = useContext(ApiContext);
  if (value === null) {
    throw new Error('Use <ApiClientProvider> acima do componente que consome a API.');
  }
  return value;
}

export function useApiClient(): ApiClient {
  return useApiContext().client;
}

/** As fabricas de opcoes, para quem prefere `useQuery(queries.catalog.product(...))`. */
export function useApiQueries(): ApiQueries {
  return useApiContext().queries;
}

// ---------------------------------------------------------------------------
// Leitura
// ---------------------------------------------------------------------------

export function useStores() {
  const { queries } = useApiContext();
  return useQuery(queries.stores.list());
}

export function useStore(slug: string) {
  const { queries } = useApiContext();
  return useQuery(queries.stores.bySlug(slug));
}

export function useCategories() {
  const { queries } = useApiContext();
  return useQuery(queries.catalog.categories());
}

export function useProduct(slug: string, storeId: string, hasSession: boolean) {
  const { queries } = useApiContext();
  return useQuery(queries.catalog.product(slug, storeId, hasSession));
}

export function useDeliveryZones(storeId: string) {
  const { queries } = useApiContext();
  return useQuery(queries.delivery.zones(storeId));
}

export function useCart() {
  const { queries } = useApiContext();
  return useQuery(queries.cart.current());
}

export function useMyOrders(query?: Parameters<ApiQueries['orders']['list']>[0]) {
  const { queries } = useApiContext();
  return useQuery(queries.orders.list(query));
}

/**
 * A fila do Painel — polling de **5 s** (ADR-0013).
 *
 * `refetchInterval` recebe o intervalo ja ajustado quando o orcamento de rate limit aperta
 * (`API §9`, item 6). Quem sabe do header `RateLimit` e o cliente HTTP; a tela so recebe o
 * numero.
 */
export function useStoreQueue(
  query: Parameters<ApiQueries['storePanel']['queue']>[0],
  intervalMs: number = STORE_QUEUE_POLL_INTERVAL_MS,
) {
  const { queries } = useApiContext();
  return useQuery({ ...queries.storePanel.queue(query), refetchInterval: intervalMs });
}

// ---------------------------------------------------------------------------
// Escrita
// ---------------------------------------------------------------------------

/**
 * A cotacao.
 *
 * `retry: false` porque `POST /v1/checkout/quote` nao esta na tabela de idempotencia do
 * `API §3.1` e a retentativa de rede ja e do cliente HTTP (`rate-limit.ts`), que conhece
 * `Retry-After` e o jitter. Deixar o React Query repetir por cima seria uma segunda politica de
 * retentativa, cega ao header.
 */
export function useQuoteMutation(): UseMutationResult<
  Awaited<ReturnType<ApiClient['checkout']['quote']>>,
  Error,
  Parameters<ApiClient['checkout']['quote']>[0]
> {
  const { client } = useApiContext();
  return useMutation({
    mutationKey: ['checkout', 'quote'],
    mutationFn: (body: Parameters<ApiClient['checkout']['quote']>[0]) => client.checkout.quote(body),
    retry: false,
  });
}

/** `POST /v1/orders`. A chave de idempotencia e do cliente HTTP — a tela nao a ve. */
export function useCreateOrder() {
  const { client } = useApiContext();
  return useMutation({
    mutationKey: ['orders', 'create'],
    mutationFn: (body: Parameters<ApiClient['orders']['create']>[0]) => client.orders.create(body),
    retry: false,
  });
}

/**
 * Adicionar item ao carrinho.
 *
 * Sem `retry`: o `If-Match` ja resolve a concorrencia, e repetir uma escrita que perdeu a
 * versao e exatamente o sobrescrever que `API §4.2` recusa. Em `409`, o erro que chega ja
 * carrega o carrinho atual (`CartVersionConflictError.current`) e o cofre de versoes ja foi
 * atualizado — a tela so precisa mostrar o que mudou (`PWA §8.5`).
 */
export function useAddCartItem() {
  const { client } = useApiContext();
  return useMutation({
    mutationKey: ['cart', 'items', 'add'],
    mutationFn: (body: Parameters<ApiClient['cart']['addItem']>[0]) => client.cart.addItem(body),
    retry: false,
  });
}
